import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router';
import { Link, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button, Field, IconButton, TextInput } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { useTranslationPrefix } from '@/translations/i18n';
import { validatePassword } from '@/utils/validators';

const ConfirmPasswordReset: React.FC = () => {
  const t = useTranslationPrefix('reset_password.confirm');
  const validationT = useTranslationPrefix('validation');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirmResetPassword, isConfirmingResetPassword } = useAuthContext();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>();

  // DEV ONLY: preview without real email/code query params
  const DEV_MOCK = import.meta.env.DEV && (!searchParams.get('email') || !searchParams.get('code'));
  const email = DEV_MOCK ? 'preview@hauserhq.com' : searchParams.get('email');
  const code = DEV_MOCK ? 'MOCK_CODE' : searchParams.get('code');

  const handlePasswordResetConfirmation = async (values: { password: string; passwordConfirm: string }) => {
    if (!email || !code) return;

    try {
      setError(undefined);
      await confirmResetPassword(email, values.password, code);
      navigate('/login');
    } catch (error: any) {
      setError(error.message);
    }
  };

  const onTogglePasswordClick = () => setShowPassword((show) => !show);
  const onToggleConfirmPasswordClick = () => setShowConfirmPassword((show) => !show);

  const validationSchema = yup.object({
    password: yup
      .string()
      .min(8, validationT('min_string', { name: t('new_password'), min: 8 }))
      .required(validationT('required', { name: t('new_password') }))
      .test('isValidPassword', validationT('password'), validatePassword),
    passwordConfirm: yup
      .string()
      .oneOf([yup.ref('password')], validationT('passwords_do_not_match'))
      .required(validationT('required', { name: t('confirm_password') })),
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      passwordConfirm: '',
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    onSubmit: handlePasswordResetConfirmation,
  });

  if (!email || !code) {
    return <Navigate to="/login" />;
  }

  // a password field: the house text input with its show/hide toggle at the end
  const passwordInput = (props: {
    name: 'password' | 'passwordConfirm';
    show: boolean;
    onToggle: () => void;
    placeholder: string;
    autoFocus?: boolean;
    error?: string | false;
  }) => (
    <TextInput
      type={props.show ? 'text' : 'password'}
      name={props.name}
      required
      placeholder={props.placeholder}
      autoComplete="new-password"
      autoFocus={props.autoFocus}
      value={formik.values[props.name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={Boolean(props.error)}
      inputProps={{ 'aria-label': props.placeholder }}
      endAdornment={
        <IconButton
          icon={props.show ? 'eye-off' : 'eye'}
          label={t('toggle_password')}
          size="sm"
          variant="ghost"
          tipPlacement={false}
          onClick={props.onToggle}
        />
      }
      sx={{ pr: '4px' }}
    />
  );
  const fieldError = (msg?: string | false) => (msg ? <span className="hx-auth-field-error">{msg}</span> : null);
  const passwordError = formik.touched.password && formik.errors.password;
  const confirmError = formik.touched.passwordConfirm && formik.errors.passwordConfirm;

  return (
    <AuthShell
      title="Reset password"
      footer={
        <Link className="hx-auth-link" to="/login">
          {t('back_to_login')}
        </Link>
      }>
      <style>{`.hx-auth-field-error { font-size: 11.5px; font-weight: 500; color: #DB5282; }`}</style>
      <form onSubmit={formik.handleSubmit} data-testid="confirm-password-reset-form">
        <h1 className="hx-auth-title">{t('title')}</h1>
        <p className="hx-auth-sub">{t('description')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label="New password"
            hint={passwordError ? undefined : 'Min. 8 characters, with uppercase, lowercase, a number and a symbol.'}>
            {passwordInput({
              name: 'password',
              show: showPassword,
              onToggle: onTogglePasswordClick,
              placeholder: t('new_password'),
              autoFocus: true,
              error: passwordError,
            })}
            {fieldError(passwordError)}
          </Field>
          <Field label="Confirm password">
            {passwordInput({
              name: 'passwordConfirm',
              show: showConfirmPassword,
              onToggle: onToggleConfirmPasswordClick,
              placeholder: t('confirm_password'),
              error: confirmError,
            })}
            {fieldError(confirmError)}
          </Field>
          {error && <p className="hx-auth-error">{error}</p>}
          <Button type="submit" variant="primary" block disabled={isConfirmingResetPassword}>
            {t('submit')}
          </Button>
        </div>
      </form>
    </AuthShell>
  );
};

export default ConfirmPasswordReset;
